'use client';
import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useScroll, useTransform } from 'motion/react';
import { Target, Compass, Gem, Palette, Code2, Megaphone, ArrowUpRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlobalTitle from '@shared/GlobalTitle';
import CredentialsMarquee from '@shared/CredentialsMarquee';
import { PlatformReviewCards } from '@shared/PlatformReviewsSection';
import SectionShapes from "@shared/SectionShapes";
import Counter from "@shared/Counter";
import { counterData } from "@data/counterData";
import { aiLogos } from "@data/socialMediaData";

/* ─── animation variants ─────────────────────────────────────────── */
const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const fadeRight = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const fadeLeft = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
};

/* ─── data ───────────────────────────────────────────────────────── */
const pillars = [
    {
        icon: Target,
        number: '01',
        title: 'Our Mission Statement',
        image: '/images/about/About-3.webp',
        body: "At VelanApps, our mission is clear: to shape your digital future and making it future-proof by coupling creativity with cutting-edge technology. We are committed to transforming businesses' visions into digital reality, regardless of the industry and size of your business. With a talented team of creative personnel, technology enthusiasts, and other niche skill specialists, we make innovation accessible to all, one project at a time.",
        quote: null,
        values: null,
    },
    {
        icon: Compass,
        number: '02',
        title: 'Our Journey',
        image: '/images/about/About-4.webp',
        body: 'VelanApps is a subsidiary of Velan Info Services, a name synonymous with innovation and excellence. With roots that extend deep into the digital domain, our journey has been marked by milestones that showcase our growth and evolution. From our early days of experimenting with various technologies to becoming a recognized name in the global design industry, we have come a long way.',
        quote: '"We Deliver Values Every Step of the Way"',
        values: null,
    },
    {
        icon: Gem,
        number: '03',
        title: 'Our Values',
        image: '/images/about/About-5.webp',
        body: null,
        quote: null,
        values: [
            { term: 'Excellence', desc: 'We strive for nothing short of excellence in every project we undertake. Our attention to detail and commitment to quality truly set us apart.' },
            { term: 'Innovation', desc: 'We believe in staying at the forefront of technological advancements, ensuring our clients receive solutions that are future-proof.' },
            { term: 'Collaboration', desc: 'We work as a symbiotic ecosystem, fusing diverse talents for superior results.' },
        ],
    },
];

const services = [
    {
        icon: Palette,
        number: '01',
        title: 'Design',
        body: 'We create captivating and functional digital experiences. From identity branding to web and mobile app design, our team of artists and designers crafts digital work of art.',
    },
    {
        icon: Code2,
        number: '02',
        title: 'Build',
        body: 'Our software development and consulting services bring your ideas to life. We develop web and mobile sites, e-commerce portals, applications, and provide collaboration services.',
    },
    {
        icon: Megaphone,
        number: '03',
        title: 'Promote',
        body: 'Our digital marketing expertise is your key to success. We excel in social media management, SEM & AdWords campaigns, and organic SEO, helping your brand thrive.',
    },
];

function PillarCard({ pillar, index, scrollYProgress }) {
    const Icon = pillar.icon;
    const isEven = index % 2 === 1;
    const start = 0.05 + index * 0.22;
    const end = start + 0.28;
    const y = useTransform(scrollYProgress, [start, end], [80, 10]);
    const opacity = useTransform(scrollYProgress, [start, end], [0, 1.5]);

    const labelSide = (
        <div className="relative flex flex-col justify-between p-10 md:p-12 overflow-hidden shrink-0 w-full md:w-[38%]">
            <Image
                src={pillar.image}
                alt="VelanApps Mission Statement"
                fill
                sizes="(max-width: 768px) 100vw, 40vw"
                draggable="false"
                quality={80}
                className="object-cover object-center"
                aria-hidden
            />
            <div className={`absolute inset-0 ${isEven ? 'bg-primary/80' : 'bg-gray-700/70'}`} />
            <div className="relative z-10 mt-16">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-5">
                    <Icon size={22} className="text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white leading-snug">{pillar.title}</h3>
            </div>
        </div>
    );

    const contentSide = (
        <div className="flex-1 bg-[#f7f8fc] p-10 md:p-12 flex flex-col justify-center space-y-5">
            {pillar.body && <p className="text-muted-foreground leading-relaxed">{pillar.body}</p>}
            {pillar.values && (
                <ul className="space-y-4">
                    {pillar.values.map((v) => (
                        <li key={v.term} className="flex gap-3">
                            <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                                <Check size={10} className="text-primary" />
                            </span>
                            <p className="text-muted-foreground leading-relaxed">
                                <span className="text-gray-900 font-semibold">{v.term}: </span>
                                {v.desc}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
            {pillar.quote && (
                <p className="text-primary italic font-semibold border-l-[3px] border-primary/40 pl-5 text-xl">
                    {pillar.quote}
                </p>
            )}
        </div>
    );

    return (
        <motion.div
            style={{ y, opacity }}
            className="flex flex-col md:flex-row overflow-hidden rounded-2xl shadow-md min-h-[260px]"
        >
            {isEven ? <>{contentSide}{labelSide}</> : <>{labelSide}{contentSide}</>}
        </motion.div>
    );
}

/* ─── component ──────────────────────────────────────────────────── */
export default function About() {
    const section3Ref = useRef(null);
    const { scrollYProgress: s3Progress } = useScroll({
        target: section3Ref,
        offset: ['start 80%', 'end 20%'],
    });

    return (
        <>
            {/* ══ Section 1 — Light hero ══════════════════════════════════ */}
            <section className="relative bg-white overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle,_#e5e7eb_1px,_transparent_1px)] bg-[size:32px_32px] opacity-40" />
                <div className="absolute top-0 right-0 w-[700px] h-[500px] bg-primary/5 rounded-full blur-[140px] pointer-events-none" />

                <div className="relative z-10 max-w-[1440px] mx-auto px-6 pt-20 pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <motion.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                            className="space-y-6"
                        >
                            <motion.div variants={fadeUp}>
                                <GlobalTitle
                                    topText="Our Story"
                                    titleText="About"
                                    coloredText="VelanApps"
                                    classes="text-left"
                                />
                            </motion.div>

                            <motion.p variants={fadeRight} className="text-muted-foreground">
                                Combination of growth opportunities, open culture and great work environment make Velan a place to build your career. Velan offers challenging assignments to prove yourself and grow with the organization. Transparency in open positions at Velan provides the best opportunity for job change and career growth.
                            </motion.p>
                            <motion.p variants={fadeRight} className="text-muted-foreground">
                                Open door policy enables employees to address their issues and feedback at appropriate forums. Velan&apos;s greatest asset is its employees — striving to hire and promote people on the basis of their qualifications, skills and performance, providing a work environment free of any form of discrimination.
                            </motion.p>

                            <div className="flex flex-col gap-5 pt-1">
                                {/* Primary CTA row */}
                                <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
                                    <Link href="/contact-us" className="w-full sm:w-auto">
                                        <Button size="lg" className="w-full sm:w-auto bg-red-gradient border-0 py-6 px-7 btn-glass group cursor-pointer">
                                            <span className="relative z-10 flex items-center justify-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                                                Start Your Journey
                                                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                                            </span>
                                        </Button>
                                    </Link>
                                    <Link href="/portfolio" className="group flex items-center gap-1.5 text-sm font-semibold text-secondary/80 hover:text-secondary transition-colors duration-200">
                                        View Our Portfolio
                                        <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                                    </Link>
                                </motion.div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="relative"
                            variants={fadeLeft}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <span className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-primary/50 rounded-tl-xl z-10" />
                            <span className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-primary/50 rounded-br-xl z-10" />

                            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl shadow-gray-200">
                                <Image
                                    src="/images/about/About-8.webp"
                                    alt="About VelanApps"
                                    fill
                                    quality={80}
                                    draggable="false"
                                    className="object-cover object-center"
                                />
                            </div>

                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.4, duration: 0.5 }}
                                viewport={{ once: true }}
                                className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-10 max-w-[calc(100%-1.5rem)] sm:max-w-none bg-primary text-white rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-5 shadow-xl shadow-primary/30"
                            >
                                {/* AI logos row */}
                                <div className="ask-ai">
                                    <p className="text-[10px] sm:text-xs font-semibold text-white/90 tracking-widest uppercase mb-2 sm:mb-3 text-center">Ask AI about VelanApps</p>
                                    <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                                        {aiLogos.map((s) => (
                                            <a
                                                key={s.title}
                                                href={s.href}
                                                aria-label={s.title}
                                                title={s.title}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors duration-200"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    {s.svg}
                                                </svg>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Counter data={counterData} />

            {/* ══ Section 2 — About VelanApps ════════════════════════════ */}
            <section className="relative bg-[#f7f8fc] py-12 md:py-20 overflow-hidden">
                <SectionShapes variant="dark" />
                <div className="relative max-w-[1440px] mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            className="relative"
                            variants={fadeRight}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <span className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-primary/50 rounded-tl-xl z-10" />
                            <span className="absolute -bottom-4 -right-4 w-12 h-12 border-b-[3px] border-r-[3px] border-primary rounded-br-lg z-10" />

                            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-gray-200">
                                <Image
                                    src="/images/about/About-2.webp"
                                    alt="VelanApps Team"
                                    fill
                                    sizes="(max-width: 1024px) 100vw, 50vw"
                                    quality={80}
                                    draggable="false"
                                    className="object-cover object-center"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 to-transparent" />
                            </div>
                        </motion.div>
                        <motion.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                            className="space-y-7"
                        >
                            <motion.div variants={fadeUp}>
                                <GlobalTitle
                                    topText="Making Your Digital Journey"
                                    titleText="Exciting And"
                                    coloredText="Future-Proof"
                                    classes="text-left"
                                />
                            </motion.div>
                            <motion.p variants={fadeUp} className="text-muted-foreground">
                                Velan feels it is important that employees feel a sense of achievement and job satisfaction. Also, it is our goal to provide opportunity add value to each and every individual by way of allowing them develop and acquire knowledge and skills day-in day-out.
                            </motion.p>
                            <motion.div variants={fadeUp} className="pt-1 space-y-3">
                                <p className="text-xs font-semibold text-muted-foreground tracking-[0.15em] mb-0 uppercase">Our Credentials</p>
                                <CredentialsMarquee className="bg-transparent" showMask={false} cardHeight="h-[80px]" />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ══ Section 3 — Mission / Journey / Values (scroll bottom-to-top) ══ */}
            <section ref={section3Ref} className="relative bg-white py-12 md:py-20 overflow-hidden">
                <div className="absolute top-0 right-0 w-[700px] h-[500px] bg-primary/5 rounded-full blur-[140px] pointer-events-none" />

                <div className="max-w-[1440px] mx-auto px-6 mb-6">
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <motion.div variants={fadeUp}>
                            <GlobalTitle
                                topText="What Drives Us"
                                titleText="Our Core"
                                coloredText="Pillars"
                                classes="text-center"
                                topTextClass="justify-center"
                            />
                        </motion.div>
                    </motion.div>
                </div>

                <div className="max-w-[1440px] mx-auto px-6 space-y-4">
                    {pillars.map((p, i) => (
                        <PillarCard
                            key={p.number}
                            pillar={p}
                            index={i}
                            scrollYProgress={s3Progress}
                        />
                    ))}
                </div>
            </section>

            {/* ══ Section 4 ══ */}
            <section className="relative bg-gray-950 overflow-hidden py-12 md:py-20">
                <div className="absolute top-0 right-0 w-[700px] h-[500px] bg-primary/5 rounded-full blur-[140px] pointer-events-none" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[640px] h-[280px] rounded-full bg-primary/8 blur-[110px]" />
                </div>
                <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                    <SectionShapes variant="dark" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <motion.div
                            className="space-y-8"
                            variants={stagger}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <motion.div className='mb-0' variants={fadeUp}>
                                <GlobalTitle
                                    topText="What's Next?"
                                    titleText="The Future We're"
                                    coloredText="Building Together"
                                    classes="text-left text-white"
                                />
                            </motion.div>

                            <motion.p variants={fadeUp} className="text-gray-300 text-md leading-relaxed">
                                As we continue our journey, VelanApps is poised for even greater heights. Our dedication to enhancing the digital landscape, one project at a time, remains steadfast. We would love for you to be a part of our future as we expand our horizons, explore new technologies, and create even more captivating digital experiences.
                            </motion.p>

                            <motion.div variants={fadeUp}>
                                <Link href="/contact-us">
                                    <Button size="lg" className="bg-red-gradient border-0 py-6 px-8 btn-glass group cursor-pointer text-base">
                                        <span className="relative z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                                            Start Your Journey
                                            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                                        </span>
                                    </Button>
                                </Link>
                            </motion.div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
                        >
                            <p className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-4">Trusted Across Platforms</p>
                            <PlatformReviewCards variant="dark" size="sm" itemWidth="w-[calc(50%-0.5rem)]" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ══ Section 5 ══ */}
            <section className="relative bg-[#f7f8fc] py-12 md:py-20 overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-6">
                    <SectionShapes variant="dark" />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                        <motion.div
                            className="lg:sticky lg:top-32 space-y-5"
                            variants={stagger}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <motion.div variants={fadeUp}>
                                <GlobalTitle
                                    topText="What We Do"
                                    titleText="Our Services Span A Wide Spectrum Of Your"
                                    coloredText="Digital Needs"
                                    classes="text-left"
                                />
                            </motion.div>
                            <motion.p variants={fadeUp} className="text-muted-foreground">
                                From concept to launch — we design, build, and promote digital products that move businesses forward.
                            </motion.p>
                        </motion.div>

                        <motion.div
                            className="space-y-4"
                            variants={stagger}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            {services.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <motion.div
                                        key={s.number}
                                        variants={fadeLeft}
                                        className="group flex gap-6 p-6 rounded-2xl bg-white border border-gray-100 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
                                    >
                                        <span className="flex-shrink-0 text-4xl font-black text-gray-100 group-hover:text-primary/20 transition-colors duration-300 leading-none mt-1 select-none">
                                            {s.number}
                                        </span>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                                                    <Icon size={15} className="text-primary group-hover:text-white transition-colors duration-300" />
                                                </div>
                                                <h3 className="font-bold text-gray-900 text-lg">{s.title}</h3>
                                            </div>
                                            <p className="text-muted-foreground text-sm leading-relaxed">{s.body}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </div>
                </div>
            </section>
        </>
    );
}
